import React, { useState, useEffect } from 'react';
import './CustomOrderModals.css';
import { customOrderAPI } from '../../../config/custom-order-api';
import { orderAPI } from '../../../config/order-api';
import { dispatchAPI } from '../../../config/dispatch-api';
import { toast } from 'react-toastify';
import { Pencil } from 'lucide-react';
import { ORDER_STATUS, getStatusLabel } from '../../../constants/general';
import { formatDateOnly, formatDateTime } from '../../../utils/utils';
import { ADMIN_PERMISSIONS } from '../../../constants/permissions';
import { helpers } from '../../../utils/helpers';
import { API_CONFIG } from '../../../config/api';
import MapPickerModal from './MapPickerModal';

const ZONE_OPTIONS = [
  { value: 'sailia to lusail route', label: 'Sailia to Lusail Route' },
  { value: 'mesaieed and wakrah route', label: 'Mesaieed and Wakrah Route' },
  { value: 'abu nakla to doha route', label: 'Abu Nakla to Doha Route' },
];

const parseStartTimeFromValue = (startTime) => {
  if (!startTime) return 0;
  const [hours, minutes] = String(startTime).split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
};

const parseStartTimeFromSlotName = (slotLabel) => {
  const startTime = String(slotLabel || '').split('-')[0]?.trim();
  const match = startTime.match(/^(\d{1,2})(?:[.:](\d{1,2}))?\s*(AM|PM)$/i);
  if (!match) return Number.MAX_SAFE_INTEGER;

  let hours = Number(match[1]);
  const minutes = Number(match[2] || 0);
  const meridiem = match[3].toUpperCase();
  if (meridiem === 'PM' && hours !== 12) hours += 12;
  if (meridiem === 'AM' && hours === 12) hours = 0;
  return (hours * 60) + minutes;
};

const normalizeFormTimeSlots = (slots = []) => {
  return slots
    .map((item) => {
      const slot = item?.timeSlot || item;
      if (!slot?.name) return null;
      return {
        _id: slot._id,
        name: slot.name,
        startTime: slot.startTime || '',
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const aMinutes = a.startTime ? parseStartTimeFromValue(a.startTime) : parseStartTimeFromSlotName(a.name);
      const bMinutes = b.startTime ? parseStartTimeFromValue(b.startTime) : parseStartTimeFromSlotName(b.name);
      return aMinutes - bMinutes;
    });
};

const CUSTOM_ORDER_REQUIRED_FIELDS = [
  { key: 'orderId', label: 'Order ID' },
  { key: 'customerName', label: 'Customer Name' },
  { key: 'customerPhone', label: 'Customer Phone' },
  { key: 'totalPrice', label: 'Total Price (QR)' },
  { key: 'timeSlot', label: 'Time Slot' },
  { key: 'mapLink', label: 'Map Link' },
  { key: 'address', label: 'Full Address' },
  { key: 'building', label: 'Building Number' },
  { key: 'street', label: 'Street' },
  { key: 'zoneNumber', label: 'Zone Number' },
  { key: 'zone', label: 'Zone' },
];

const validateCustomOrderForm = (formData, resolvedCoords = null) => {
  for (const field of CUSTOM_ORDER_REQUIRED_FIELDS) {
    if (field.key === 'orderId' && formData.type !== 'department') {
      continue;
    }
    const rawValue = formData[field.key];
    const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;

    if (field.key === 'totalPrice') {
      if (value === '' || value === null || value === undefined) {
        return { valid: false, message: `Please enter ${field.label}` };
      }
      const totalPrice = Number(value);
      if (!Number.isFinite(totalPrice) || totalPrice <= 0) {
        return { valid: false, message: 'Please enter a valid total price' };
      }
      continue;
    }

    if (field.key === 'mapLink' || field.key === 'exactMapLink') {
      const hasCoords = resolvedCoords || (value && extractCoordinatesFromUrl(value));
      if (!hasCoords) {
        return { valid: false, message: 'Please enter a valid Google Maps link or pick a location on the map.' };
      }
      continue;
    }

    if (!value) {
      if (field.key === 'timeSlot' || field.key === 'zone') {
        return { valid: false, message: `Please select ${field.label.toLowerCase()}` };
      }
      return { valid: false, message: `Please enter ${field.label.toLowerCase()}` };
    }
  }

  if (!/^\d{8}$/.test(String(formData.customerPhone || '').trim())) {
    return { valid: false, message: 'Phone number must be exactly 8 digits' };
  }
 
  return { valid: true };
};

const getStatusSubtext = (status, packingStatus) => {
  switch (status) {
    case ORDER_STATUS.PENDING:
      return 'Custom order is pending confirmation';
    case ORDER_STATUS.CONFIRMED:
      return packingStatus === 'packed'
        ? 'Order is packed and ready for delivery'
        : 'Order is confirmed and being packed';
    case ORDER_STATUS.ON_DELIVERY:
      return 'Order is on the way for delivery';
    case ORDER_STATUS.DELIVERED:
      return 'Order delivered successfully';
    case ORDER_STATUS.CANCELLED:
      return 'Order cancelled';
    default:
      return 'Custom order is active';
  }
};

const buildCustomOrderTimeline = (order) => {
  const timeline = [];

  if (order?.createdAt) {
    timeline.push({ label: 'Order Placed', time: formatDateTime(order.createdAt) });
  }
  if (order?.staffAssignedAt) {
    timeline.push({ label: 'Staff Assigned', time: formatDateTime(order.staffAssignedAt) });
  }
  if (order?.packedAt) {
    timeline.push({ label: 'Order Packed', time: formatDateTime(order.packedAt) });
  }
  if (order?.orderStatus === ORDER_STATUS.ON_DELIVERY) {
    timeline.push({ label: 'Out For Delivery', time: '-' });
  }
  if (order?.deliveredOn) {
    timeline.push({ label: 'Delivered', time: formatDateTime(order.deliveredOn) });
  }
  if (order?.orderStatus === ORDER_STATUS.CANCELLED) {
    timeline.push({ label: 'Cancelled', time: order.updatedAt ? formatDateTime(order.updatedAt) : '-' });
  }

  return timeline;
};

const formatDeliveryDateDisplay = (dateValue) => {
  if (!dateValue) return '-';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const CustomOrderModals = ({
  adminData,
  drivers = [],
  overlayOrderId = null,
  showCreateModal: showCreateModalProp = false,
  onOverlayClose = null,
  onOrderChange = null,
  onEditInDispatch = null,
  dispatchLoading = false,
}) => {
  const canCreate = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.CREATE_CUSTOM_ORDERS);
  const canEdit = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.EDIT_CUSTOM_ORDERS);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Edit states for custom order details modal
  const [isEditingCustomer, setIsEditingCustomer] = useState(false);
  const [editCustomerName, setEditCustomerName] = useState('');
  const [editCustomerPhone, setEditCustomerPhone] = useState('');

  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [editAddressZone, setEditAddressZone] = useState('');
  const [editAddressBuilding, setEditAddressBuilding] = useState('');
  const [editAddressZoneNumber, setEditAddressZoneNumber] = useState('');
  const [editAddressStreet, setEditAddressStreet] = useState('');
  const [editAddressFull, setEditAddressFull] = useState('');
  const [editAddressExactMapLink, setEditAddressExactMapLink] = useState('');

  const [isEditingDelivery, setIsEditingDelivery] = useState(false);
  const [editDeliveryDate, setEditDeliveryDate] = useState('');
  const [editTimeSlot, setEditTimeSlot] = useState('');

  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editNotes, setEditNotes] = useState('');

  const [isEditingBill, setIsEditingBill] = useState(false);
  const [editTotalPrice, setEditTotalPrice] = useState('');

  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [editedStatus, setEditedStatus] = useState('');

  const [showStaffSelectModal, setShowStaffSelectModal] = useState(false);
  const [staffAssignmentType, setStaffAssignmentType] = useState(null);
  const [staffModalMode, setStaffModalMode] = useState('assign');
  const [staffActionLoading, setStaffActionLoading] = useState(false);

  const [showFormModal, setShowFormModal] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formTimeSlots, setFormTimeSlots] = useState([]);
  const [loadingFormSlots, setLoadingFormSlots] = useState(false);

  const initialFormState = {
    orderId: '',
    customerName: '',
    customerPhone: '',
    totalPrice: '',
    timeSlot: '',
    timeSlotId: '',
    mapLink: '',
    exactMapLink: '',
    address: '',
    building: '',
    street: '',
    zone: '',
    zoneNumber: '',
    type: 'department',
  };
  const [formData, setFormData] = useState(initialFormState);

  const [showMapPicker, setShowMapPicker] = useState(false);
  const [isEditPicker, setIsEditPicker] = useState(false);
  const [coordsModalData, setCoordsModalData] = useState(null); // { lat, lng, isEdit }
  const [resolvingShortUrl, setResolvingShortUrl] = useState(false);
  const [isEditAddressLocationUpdated, setIsEditAddressLocationUpdated] = useState(false);
  const [resolvedCoords, setResolvedCoords] = useState(null);
  const [editResolvedCoords, setEditResolvedCoords] = useState(null);

  const cleanQatarPhone = (phone) => {
    if (!phone) return '';
    const cleaned = String(phone).replace(/\s+/g, '');
    if (cleaned.startsWith('+974')) {
      return cleaned.slice(4);
    }
    if (cleaned.startsWith('974') && cleaned.length > 8) {
      return cleaned.slice(3);
    }
    return cleaned;
  };

  const handlePhoneBlur = (e) => {
    const value = e.target.value.trim();
    if (!value) {
      toast.error('Customer phone is required');
    } else if (!/^\d{8}$/.test(value)) {
      toast.error('Phone number must be exactly 8 digits');
    }
  };

  const extractCoordinatesFromUrl = (url) => {
    if (!url) return null;
    const cleanUrl = url.trim();

    // 1. Check if already coordinate format: "25.123, 51.123"
    const coordMatch = cleanUrl.match(/^(-?[\d\.]+)\s*,\s*(-?[\d\.]+)$/);
    if (coordMatch) {
      return { lat: parseFloat(coordMatch[1]), lng: parseFloat(coordMatch[2]) };
    }

    // 2. Check for !3d...!4d pattern (standard Google redirect URL)
    const dMatch = cleanUrl.match(/!3d(-?[\d\.]+)!4d(-?[\d\.]+)/);
    if (dMatch) {
      return { lat: parseFloat(dMatch[1]), lng: parseFloat(dMatch[2]) };
    }

    // 3. Check for @lat,lng pattern
    const atMatch = cleanUrl.match(/@(-?[\d\.]+),(-?[\d\.]+)/);
    if (atMatch) {
      return { lat: parseFloat(atMatch[1]), lng: parseFloat(atMatch[2]) };
    }

    // 4. Check for q=lat,lng pattern
    const qMatch = cleanUrl.match(/[?&]q=(-?[\d\.]+),(-?[\d\.]+)/);
    if (qMatch) {
      return { lat: parseFloat(qMatch[1]), lng: parseFloat(qMatch[2]) };
    }

    return null;
  };

  const getCoordinatesStatus = (url, coordsState) => {
    if (resolvingShortUrl) {
      return { info: true, message: "Resolving coordinates..." };
    }
    if (coordsState) {
      return { success: true, lat: coordsState.lat, lng: coordsState.lng };
    }
    if (!url || !url.trim()) return null;
    const cleanUrl = url.trim();
    const coords = extractCoordinatesFromUrl(cleanUrl);
    if (coords) {
      return { success: true, lat: coords.lat, lng: coords.lng };
    }
    return { warning: true, message: "Cannot parse coordinates from this link. Please pick location on map." };
  };

  const resolveCoordinatesFromMapLink = async (url) => {
    if (!url || !url.trim()) return null;

    const cleanUrl = url.trim();
    const localCoords = extractCoordinatesFromUrl(cleanUrl);
    if (localCoords) return localCoords;

    const response = await customOrderAPI.extractCoordinates(cleanUrl);
    if (response.success && response.coordinates) {
      return response.coordinates;
    }

    throw new Error(response.msg || "Could not resolve coordinates.");
  };

  const handleMapLinkInputChange = async (e) => {
    const val = e.target.value;
    setFormData(prev => ({ ...prev, mapLink: val }));
    setResolvedCoords(null);

    if (!val || !val.trim()) return;
    const cleanUrl = val.trim();

    const coords = extractCoordinatesFromUrl(cleanUrl);
    if (coords) {
      setResolvedCoords(coords);
      setFormData(prev => ({
        ...prev,
        exactMapLink: cleanUrl.includes('http') ? cleanUrl : `https://maps.google.com/?q=${coords.lat},${coords.lng}`
      }));
      return;
    }

    if (cleanUrl.includes('google.') || cleanUrl.includes('goo.gl') || cleanUrl.includes('maps.app.goo.gl')) {
      try {
        setResolvingShortUrl(true);
        const backendCoords = await resolveCoordinatesFromMapLink(cleanUrl);
        setResolvedCoords(backendCoords);
        setFormData(prev => ({
          ...prev,
          exactMapLink: cleanUrl
        }));
      } catch (err) {
        console.error("Auto map-link extraction failed:", err);
      } finally {
        setResolvingShortUrl(false);
      }
    }
  };

  const handleEditMapLinkInputChange = async (e) => {
    const val = e.target.value;
    setEditAddressExactMapLink(val);
    setEditResolvedCoords(null);
    setIsEditAddressLocationUpdated(true);

    if (!val || !val.trim()) return;
    const cleanUrl = val.trim();

    const coords = extractCoordinatesFromUrl(cleanUrl);
    if (coords) {
      setEditResolvedCoords(coords);
      return;
    }

    if (cleanUrl.includes('google.') || cleanUrl.includes('goo.gl') || cleanUrl.includes('maps.app.goo.gl')) {
      try {
        setResolvingShortUrl(true);
        const backendCoords = await resolveCoordinatesFromMapLink(cleanUrl);
        setEditResolvedCoords(backendCoords);
      } catch (err) {
        console.error("Auto map-link extraction failed:", err);
      } finally {
        setResolvingShortUrl(false);
      }
    }
  };

  const applyExtractedCoordinates = (lat, lng, originalUrl, isEdit) => {
    const formattedLink = originalUrl.includes('http') ? originalUrl : `https://maps.google.com/?q=${lat},${lng}`;
    if (isEdit) {
      setEditAddressExactMapLink(formattedLink);
    } else {
      setFormData(prev => ({
        ...prev,
        mapLink: formattedLink,
        exactMapLink: formattedLink
      }));
    }
    toast.success("Coordinates successfully applied!");
  };

  const handleFetchCoordinates = async (url, isEdit = false) => {
    if (!url || !url.trim()) {
      toast.warn("Please enter a map link first.");
      return;
    }
    const cleanUrl = url.trim();
    const coords = extractCoordinatesFromUrl(cleanUrl);
    if (coords) {
      applyExtractedCoordinates(coords.lat, coords.lng, cleanUrl, isEdit);
      return;
    }

    try {
      const backendCoords = await resolveCoordinatesFromMapLink(cleanUrl);
      applyExtractedCoordinates(backendCoords.lat, backendCoords.lng, cleanUrl, isEdit);
    } catch (err) {
      console.error("Backend coordinate extraction failed:", err);
      toast.error("Failed to extract coordinates from link. Please use 'Pick Location' or paste a link with coordinates.");
    }
  };

  const handleMapConfirm = (lat, lng) => {
    const coords = { lat, lng };
    const gmapsLink = `https://maps.google.com/?q=${lat},${lng}`;
    if (isEditPicker) {
      setEditResolvedCoords(coords);
      setEditAddressExactMapLink('');
      setIsEditAddressLocationUpdated(true);
    } else {
      setResolvedCoords(coords);
      setFormData(prev => ({
        ...prev,
        mapLink: '',
        exactMapLink: gmapsLink
      }));
    }
  };

  const fetchFormTimeSlots = async () => {
    try {
      setLoadingFormSlots(true);
      const response = await dispatchAPI.getDispatchDetails(new Date());
      if (response.success) {
        setFormTimeSlots(normalizeFormTimeSlots(response.data?.timeSlots || []));
      } else {
        setFormTimeSlots([]);
        toast.error(response.msg || 'Failed to load time slots');
      }
    } catch (err) {
      console.error('Error fetching form time slots:', err);
      setFormTimeSlots([]);
      toast.error('Failed to load time slots');
    } finally {
      setLoadingFormSlots(false);
    }
  };

  const fetchOrderDetails = async (orderId) => {
    if (!orderId) return;

    try {
      setModalLoading(true);
      const response = await customOrderAPI.getCustomOrderDetails(orderId);
      if (response.success) {
        setOrderDetails(response.data);
      } else {
        toast.error(response.msg || 'Failed to fetch order details');
        setSelectedOrderId(null);
      }
    } catch (err) {
      console.error('Error fetching order details:', err);
      toast.error('Error fetching order details');
      setSelectedOrderId(null);
    } finally {
      setModalLoading(false);
    }
  };

  useEffect(() => {
    if (overlayOrderId) {
      setSelectedOrderId(overlayOrderId);
    }
  }, [overlayOrderId]);

  useEffect(() => {
    if (selectedOrderId) {
      fetchOrderDetails(selectedOrderId);
    }
  }, [selectedOrderId]);

  const openCreateModal = async () => {
    setFormData(initialFormState);
    setResolvedCoords(null);
    setEditResolvedCoords(null);
    setShowFormModal(true);
    await fetchFormTimeSlots();
  };

  useEffect(() => {
    if (showCreateModalProp && canCreate) {
      openCreateModal();
    }
  }, [showCreateModalProp, canCreate]);

  const closeDetailsModal = () => {
    setSelectedOrderId(null);
    setOrderDetails(null);
    setIsEditingCustomer(false);
    setIsEditingAddress(false);
    setIsEditingDelivery(false);
    setIsEditingNotes(false);
    setIsEditingBill(false);
    setIsEditingStatus(false);
    setResolvedCoords(null);
    setEditResolvedCoords(null);
    onOverlayClose?.();
  };

  const handleStartEditCustomer = () => {
    setEditCustomerName(orderDetails.deliveryAddress?.receiversName || '');
    setEditCustomerPhone(orderDetails.deliveryAddress?.receiversPhoneNumber || '');
    setIsEditingCustomer(true);
  };

  const handleStartEditAddress = () => {
    setEditAddressZone(orderDetails.deliveryAddress?.zone || '');
    setEditAddressBuilding(orderDetails.deliveryAddress?.buildingNumber || '');
    setEditAddressZoneNumber(orderDetails.deliveryAddress?.zoneNumber || '');
    setEditAddressStreet(orderDetails.deliveryAddress?.street || '');
    setEditAddressFull(orderDetails.deliveryAddress?.fullAddress || '');
    
    const existingLink = orderDetails.deliveryAddress?.exactMapLink || '';
    const dbLat = orderDetails.deliveryAddress?.lat;
    const dbLng = orderDetails.deliveryAddress?.long || orderDetails.deliveryAddress?.lng;
    
    let initialCoords = null;
    if (dbLat && dbLng) {
      initialCoords = { lat: parseFloat(dbLat), lng: parseFloat(dbLng) };
    } else {
      initialCoords = extractCoordinatesFromUrl(existingLink);
    }
    
    setEditResolvedCoords(initialCoords);

    if (initialCoords && (existingLink.startsWith('https://maps.google.com/?q=') || !existingLink.includes('http') || !existingLink)) {
      setEditAddressExactMapLink('');
    } else {
      setEditAddressExactMapLink(existingLink);
    }
    
    setIsEditAddressLocationUpdated(false);
    setIsEditingAddress(true);
  };

  const handleStartEditDelivery = async () => {
    if (formTimeSlots.length === 0) {
      await fetchFormTimeSlots();
    }
    let formattedDate = '';
    if (orderDetails.deliveryDate) {
      const dateObj = new Date(orderDetails.deliveryDate);
      if (!Number.isNaN(dateObj.getTime())) {
        formattedDate = dateObj.toISOString().split('T')[0];
      }
    }
    setEditDeliveryDate(formattedDate);
    setEditTimeSlot(orderDetails.timeSlot || '');
    setIsEditingDelivery(true);
  };

  const handleStartEditNotes = () => {
    setEditNotes(orderDetails.notes || '');
    setIsEditingNotes(true);
  };

  const saveOrderChanges = async (updates, successMsg) => {
    try {
      setModalLoading(true);
      const response = await customOrderAPI.updateCustomOrder(getOrderMongoId(), updates);
      if (response.success) {
        toast.success(successMsg || 'Custom order updated successfully');
        if (response.data) {
          setOrderDetails(response.data);
        }
        onOrderChange?.();
      } else {
        toast.error(response.msg || 'Failed to update custom order');
      }
    } catch (err) {
      console.error('Error updating custom order:', err);
      toast.error('Error updating custom order');
    } finally {
      setModalLoading(false);
    }
  };

  const handleSaveCustomer = async () => {
    const localPhone = editCustomerPhone.trim();
    if (!localPhone) {
      toast.error('Customer phone is required');
      return;
    }
    if (!/^\d{8}$/.test(localPhone)) {
      toast.error('Phone number must be exactly 8 digits');
      return;
    }
    await saveOrderChanges({
      customerName: editCustomerName.trim(),
      customerPhone: `+974${localPhone}`,
    }, 'Customer details updated successfully');
    setIsEditingCustomer(false);
  };

  const handleSaveAddress = async () => {
    const cleanLink = editAddressExactMapLink.trim();
    let coords = editResolvedCoords || extractCoordinatesFromUrl(cleanLink);
    if (!coords) {
      try {
        setModalLoading(true);
        coords = await resolveCoordinatesFromMapLink(cleanLink);
        setEditResolvedCoords(coords);
      } catch (err) {
        toast.error(err.message || 'Please enter a valid Google Maps link or pick a location on the map.');
        setModalLoading(false);
        return;
      }
    }
    const finalLink = cleanLink || `https://maps.google.com/?q=${coords.lat},${coords.lng}`;
    await saveOrderChanges({
      zone: editAddressZone,
      building: editAddressBuilding,
      zoneNumber: editAddressZoneNumber,
      street: editAddressStreet,
      address: editAddressFull,
      exactMapLink: finalLink,
      mapLink: `${coords.lat},${coords.lng}`,
    }, 'Address details updated successfully');
    setIsEditingAddress(false);
  };

  const handleSaveDelivery = async () => {
    await saveOrderChanges({
      deliveryDate: editDeliveryDate || null,
      timeSlot: editTimeSlot,
    }, 'Delivery details updated successfully');
    setIsEditingDelivery(false);
  };

  const handleSaveNotes = async () => {
    await saveOrderChanges({
      notes: editNotes,
    }, 'Notes updated successfully');
    setIsEditingNotes(false);
  };

  const handleStartEditBill = () => {
    setEditTotalPrice(orderDetails.totalPrice || 0);
    setIsEditingBill(true);
  };

  const handleSaveBill = async () => {
    const parsedPrice = parseFloat(editTotalPrice);
    if (Number.isNaN(parsedPrice) || parsedPrice < 0) {
      toast.error('Please enter a valid price');
      return;
    }
    await saveOrderChanges({
      totalPrice: parsedPrice,
    }, 'Bill details updated successfully');
    setIsEditingBill(false);
  };

  const handleSaveStatus = async () => {
    await saveOrderChanges({
      orderStatus: editedStatus,
    }, 'Order status updated successfully');
    setIsEditingStatus(false);
  };

  const handleUpdateOrderStatus = async (newStatus) => {
    await saveOrderChanges({
      orderStatus: newStatus,
    }, `Order status updated to ${newStatus} successfully`);
  };

  const getOrderMongoId = () => orderDetails?._id || orderDetails?.id;

  const getCustomOrderCustomerName = () => (
    orderDetails?.deliveryAddress?.receiversName
    || orderDetails?.customerName
    || ''
  );

  const getShareStaffId = () => orderDetails?.deliveryBoyDetails?.id || '';

  const buildStaffShareMessage = () => {
    if (!orderDetails) return '';

    const orderMongoId = getOrderMongoId();
    const assignedStaffId = getShareStaffId();
    let riderUrl = `${API_CONFIG.STAFF_RIDER_BASE_URL}/admin/rider?id=${orderMongoId}&ordersource=wp`;
    if (assignedStaffId) {
      riderUrl += `&rid=${assignedStaffId}`;
    }
    const lines = [
      `ORDER #${orderDetails.orderId}`,
      '',
      `To: ${getCustomOrderCustomerName()}`,
    ];

    const total = Number(orderDetails.totalPrice || 0).toFixed(2);
    lines.push(`Order Total: QR ${total}`);

    lines.push('', riderUrl);
    return lines.join('\n');
  };

  const openWhatsAppShare = (phoneNumber, message) => {
    const encodedText = encodeURIComponent(message);
    const phone = phoneNumber ? String(phoneNumber).replace(/[^\d]/g, '') : '';
    const waUrl = phone ? `https://wa.me/${phone}?text=${encodedText}` : `https://wa.me/?text=${encodedText}`;
    window.open(waUrl, '_blank');
  };

  const closeStaffSelectModal = () => {
    setShowStaffSelectModal(false);
    setStaffModalMode('assign');
  };

  const handleShareToWhatsAppOther = () => {
    const message = buildStaffShareMessage();
    openWhatsAppShare(null, message);
    closeStaffSelectModal();
  };

  const handleShareToStaff = (staff) => {
    if (!staff) return;
    const message = buildStaffShareMessage();
    openWhatsAppShare(staff.phoneNumber, message);
    closeStaffSelectModal();
  };

  const getStaffModalDrivers = (mode = staffModalMode) => (
    mode === 'assign'
      ? drivers.filter((driver) => driver.status !== false)
      : drivers
  );

  const openStaffSelect = (type) => {
    setStaffAssignmentType(type);
    setStaffModalMode('assign');
    setShowStaffSelectModal(true);
  };

  const openStaffShareModal = () => {
    setStaffModalMode('share');
    setShowStaffSelectModal(true);
  };

  const handleAssignStaff = async (staff) => {
    if (!staff || !orderDetails?.orderId) return;

    const staffId = staff.id || null;
    const updates = {};
    if (staffAssignmentType === 'packing') {
      updates.packingStaff = {
        id: staffId,
        name: staff.name,
        phoneNumber: staff.phoneNumber || '',
      };
    } else {
      updates.deliveryBoyDetails = {
        id: staffId,
        name: staff.name,
        phoneNumber: staff.phoneNumber || '',
      };
    }

    try {
      setStaffActionLoading(true);
      const response = await customOrderAPI.updateCustomOrder(getOrderMongoId(), updates);
      if (response.success) {
        toast.success(`${staffAssignmentType === 'packing' ? 'Packing' : 'Delivery'} staff assigned successfully`);
        if (response.data) {
          setOrderDetails(response.data);
        }
        onOrderChange?.();
        closeStaffSelectModal();
      } else {
        toast.error(response.msg || 'Failed to update custom order');
      }
    } catch (err) {
      console.error('Error assigning staff to custom order:', err);
      toast.error('Error assigning staff');
    } finally {
      setStaffActionLoading(false);
    }
  };

  const handleApproveCancel = async () => {
    const mongoId = getOrderMongoId();
    if (!mongoId) return;

    try {
      setModalLoading(true);
      const response = await orderAPI.cancelRequestAction(mongoId, 'approve');
      if (response.success) {
        toast.success(response.msg || 'Order cancelled successfully');
        await fetchOrderDetails(selectedOrderId);
        onOrderChange?.();
      } else {
        toast.error(response.msg || 'Failed to approve cancellation');
      }
    } catch (err) {
      console.error('Error approving cancellation:', err);
      toast.error('Error approving cancellation');
    } finally {
      setModalLoading(false);
    }
  };

  const handleRejectCancel = async () => {
    const mongoId = getOrderMongoId();
    if (!mongoId) return;

    try {
      setModalLoading(true);
      const response = await orderAPI.cancelRequestAction(mongoId, 'reject');
      if (response.success) {
        toast.success(response.msg || 'Cancellation request rejected');
        await fetchOrderDetails(selectedOrderId);
        onOrderChange?.();
      } else {
        toast.error(response.msg || 'Failed to reject cancellation');
      }
    } catch (err) {
      console.error('Error rejecting cancellation:', err);
      toast.error('Error rejecting cancellation');
    } finally {
      setModalLoading(false);
    }
  };

  const handleSlotChange = (e) => {
    const slotName = e.target.value;
    const selectedSlot = formTimeSlots.find((slot) => slot.name === slotName);
    setFormData((prev) => ({
      ...prev,
      timeSlot: slotName,
      timeSlotId: selectedSlot?._id || '',
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'customerPhone') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 8);
      setFormData((prev) => ({ ...prev, [name]: digitsOnly }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    try {
      setFormLoading(true);

      const cleanMapLink = formData.mapLink.trim();
      let coords = resolvedCoords || extractCoordinatesFromUrl(cleanMapLink);
      if (!coords) {
        coords = await resolveCoordinatesFromMapLink(cleanMapLink);
        setResolvedCoords(coords);
        setFormData(prev => ({
          ...prev,
          exactMapLink: cleanMapLink
        }));
      }

      const validation = validateCustomOrderForm(formData, coords);
      if (!validation.valid) {
        toast.error(validation.message);
        return;
      }

      const selectedSlot = formTimeSlots.find((slot) => slot.name === formData.timeSlot);

      const payload = {
        orderId: formData.type === 'department' ? `DT-${formData.orderId.trim()}` : '',
        type: formData.type,
        customerName: formData.customerName.trim(),
        customerPhone: `+974${formData.customerPhone.trim()}`,
        totalPrice: parseFloat(formData.totalPrice),
        exactMapLink: formData.exactMapLink.trim() || cleanMapLink || `https://maps.google.com/?q=${coords.lat},${coords.lng}`,
        address: formData.address.trim(),
        building: formData.building.trim(),
        street: formData.street.trim(),
        zone: formData.zone.trim(),
        zoneNumber: formData.zoneNumber.trim(),
        timeSlot: formData.timeSlot.trim(),
        timeSlotId: selectedSlot?._id || formData.timeSlotId,
        deliveryDate: formatDateOnly(new Date()),
        mapLink: `${coords.lat},${coords.lng}`,
      };

      const response = await customOrderAPI.createCustomOrder(payload);

      if (response.success) {
        toast.success(response.msg || 'Successfully created custom order');
        setFormData(initialFormState);
        setShowFormModal(false);
        onOrderChange?.();
        onOverlayClose?.();
      } else {
        toast.error(response.msg || 'Operation failed');
      }
    } catch (err) {
      console.error('Error submitting custom order:', err);
      toast.error('Operation failed');
    } finally {
      setFormLoading(false);
    }
  };

  const openInMaps = (lat, long) => {
    if (!lat || !long) return;
    const url = `https://www.google.com/maps?q=${lat},${long}`;
    window.open(url, '_blank');
  };

  const timeline = orderDetails ? buildCustomOrderTimeline(orderDetails) : [];
  const deliveryAddress = orderDetails?.deliveryAddress || {};

  return (
    <>
      {canCreate && showFormModal && (
        <div className="custom-order-dialog-overlay">
          <div className="custom-order-dialog-box" onClick={(e) => e.stopPropagation()}>
            <div className="custom-order-dialog-header">
              <h2>Create Custom Order</h2>
              <button type="button" className="custom-order-close-btn" onClick={() => {
                setShowFormModal(false);
                onOverlayClose?.();
              }}>
                &times;
              </button>
            </div>
            <form onSubmit={handleFormSubmit} className="custom-order-form">
              <div className="custom-order-form-group" style={{ marginBottom: '16px' }}>
                <label className="required-field">Order Type</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  required
                >
                  <option value="department">Department</option>
                  <option value="whatsapp">Whatsapp</option>
                  <option value="replacement">Replacement</option>
                </select>
              </div>

              <div className="custom-order-form-row">
                {formData.type === 'department' ? (
                  <>
                    <div className="custom-order-form-group">
                      <label className="required-field">Order ID</label>
                      <div className="custom-order-id-input-wrapper">
                        <span className="custom-order-id-prefix">DT-</span>
                        <input
                          type="text"
                          name="orderId"
                          value={formData.orderId}
                          onChange={handleInputChange}
                          placeholder="Order ID"
                          required
                        />
                      </div>
                    </div>
                    <div className="custom-order-form-group">
                      <label className="required-field">Customer Name</label>
                      <input
                        type="text"
                        name="customerName"
                        value={formData.customerName}
                        onChange={handleInputChange}
                        placeholder="Customer Name"
                        required
                      />
                    </div>
                  </>
                ) : (
                  <div className="custom-order-form-group" style={{ gridColumn: 'span 2' }}>
                    <label className="required-field">Customer Name</label>
                    <input
                      type="text"
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleInputChange}
                      placeholder="Customer Name"
                      required
                    />
                  </div>
                )}
              </div>

              <div className="custom-order-form-row">
                <div className="custom-order-form-group">
                  <label className="required-field">Customer Phone</label>
                  <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: 'white', overflow: 'hidden', width: '100%', boxSizing: 'border-box' }}>
                    <span style={{ padding: '10px 12px', backgroundColor: '#f3f4f6', borderRight: '1px solid #d1d5db', color: '#6b7280', fontWeight: '600', fontSize: '14px', userSelect: 'none', whiteSpace: 'nowrap' }}>+974</span>
                    <input
                      type="tel"
                      name="customerPhone"
                      value={formData.customerPhone}
                      onChange={handleInputChange}
                      onBlur={handlePhoneBlur}
                      placeholder="Phone number"
                      maxLength={8}
                      required
                      style={{ border: 'none', borderRadius: 0, flex: 1, padding: '10px 12px', margin: 0, outline: 'none', boxShadow: 'none' }}
                    />
                  </div>
                </div>
                <div className="custom-order-form-group">
                  <label className="required-field">Total Price (QR)</label>
                  <input
                    type="number"
                    name="totalPrice"
                    value={formData.totalPrice}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0.01"
                    placeholder="Total Price (QR)"
                    required
                  />
                </div>
              </div>

              <div className="custom-order-form-group">
                <label className="required-field">Time Slot</label>
                <select
                  name="timeSlot"
                  value={formData.timeSlot}
                  onChange={handleSlotChange}
                  required
                  disabled={loadingFormSlots}
                >
                  <option value="">{loadingFormSlots ? 'Loading slots...' : '-- Select timeslot --'}</option>
                  {formTimeSlots.map((slot) => (
                    <option key={slot._id || slot.name} value={slot.name}>
                      {slot.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="custom-order-form-group">
                <label className="required-field">Map Link or Coordinates</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button
                    type="button"
                    className="custom-orders-map-button"
                    onClick={() => {
                      setIsEditPicker(false);
                      setShowMapPicker(true);
                    }}
                    style={{ padding: '0 16px', height: '38px', margin: 0, fontSize: '13px', display: 'flex', alignItems: 'center', backgroundColor: '#10b981', color: 'white', borderColor: '#10b981', flexShrink: 0 }}
                  >
                    Pick Location
                  </button>
                  <input
                    type="text"
                    name="mapLink"
                    value={formData.mapLink}
                    onChange={handleMapLinkInputChange}
                    placeholder="Or paste Google Maps link or coordinates here..."
                    style={{ flex: 1, margin: 0, height: '38px' }}
                  />
                </div>
                {(() => {
                  const status = getCoordinatesStatus(formData.mapLink, resolvedCoords);
                  if (!status) return null;
                  if (status.info) {
                    return (
                      <div className="custom-order-coords-below" style={{ marginTop: '8px', padding: '8px 12px', borderRadius: '6px', border: '1px solid #bfdbfe', backgroundColor: '#eff6ff', color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                        <div style={{ width: '14px', height: '14px', border: '2px solid #3b82f6', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', flexShrink: 0 }}></div>
                        <span>{status.message}</span>
                      </div>
                    );
                  }
                  if (status.success) {
                    return (
                      <div className="custom-order-coords-below" style={{ marginTop: '8px', padding: '8px 12px', borderRadius: '6px', border: '1px solid #bbf7d0', backgroundColor: '#f0fdf4', color: '#15803d', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                        <span style={{ color: '#16a34a', fontWeight: 'bold' }}>✓</span>
                        <span>Coordinates fetched successfully: {status.lat}, {status.lng}</span>
                      </div>
                    );
                  }
                  if (status.warning) {
                    return (
                      <div className="custom-order-coords-below" style={{ marginTop: '8px', padding: '8px 12px', borderRadius: '6px', border: '1px solid #fde68a', backgroundColor: '#fffbeb', color: '#b45309', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                        <span style={{ color: '#d97706', fontWeight: 'bold' }}>⚠</span>
                        <span>{status.message}</span>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>



              <div className="custom-order-form-group">
                <label className="required-field">Full Address</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Full Address"
                  required
                  className="custom-order-textarea"
                />
              </div>

              <div className="custom-order-form-row-3">
                <div className="custom-order-form-group">
                  <label className="required-field">Building Number</label>
                  <input
                    type="text"
                    name="building"
                    value={formData.building}
                    onChange={handleInputChange}
                    placeholder="Building Number"
                    required
                  />
                </div>
                <div className="custom-order-form-group">
                  <label className="required-field">Street</label>
                  <input
                    type="text"
                    name="street"
                    value={formData.street}
                    onChange={handleInputChange}
                    placeholder="Street"
                    required
                  />
                </div>
                <div className="custom-order-form-group">
                  <label className="required-field">Zone Number</label>
                  <input
                    type="text"
                    name="zoneNumber"
                    value={formData.zoneNumber}
                    onChange={handleInputChange}
                    placeholder="Zone Number"
                    required
                  />
                </div>
              </div>

              <div className="custom-order-form-group">
                <label className="required-field">Zone</label>
                <select name="zone" value={formData.zone} onChange={handleInputChange} required>
                  <option value="">-- Select Zone --</option>
                  {ZONE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="custom-order-form-actions">
                <button
                  type="button"
                  className="custom-order-cancel-btn"
                  onClick={() => {
                    setShowFormModal(false);
                    onOverlayClose?.();
                  }}
                  disabled={formLoading}
                >
                  Cancel
                </button>
                <button type="submit" className="custom-order-submit-btn" disabled={formLoading}>
                  {formLoading ? 'Loading...' : 'Create Order'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedOrderId && (
        <div
          className="custom-orders-modal-overlay custom-orders-details-modal-overlay"
          onClick={() => {
            if (!modalLoading) closeDetailsModal();
          }}
        >
          <div className="custom-orders-modal-content" onClick={(e) => e.stopPropagation()}>
            {modalLoading ? (
              <div className="custom-orders-loading-state">
                <div className="custom-orders-loading-spinner"></div>
                <p>Loading order details...</p>
              </div>
            ) : orderDetails ? (
              <>
                <div className="custom-orders-modal-header">
                  <div className="custom-orders-modal-header-left">
                    <h2>Custom Order Details {orderDetails.orderId ? `- #${orderDetails.orderId}` : ''}</h2>
                  </div>
                  <div className="custom-orders-modal-header-actions">
                    <button type="button" className="custom-orders-close-button" onClick={closeDetailsModal}>&times;</button>
                  </div>
                </div>

                <div className="custom-orders-modal-body">
                  <div className="custom-orders-card custom-orders-full-width custom-orders-status-card-simple">
                    <div className="custom-orders-order-status-card-header">
                      <h3 className="custom-orders-order-status-card-title">Order Status</h3>
                      {!isEditingStatus && canEdit && (
                        <button
                          type="button"
                          className="custom-orders-edit-toggle-btn"
                          onClick={() => {
                            setEditedStatus(orderDetails.orderStatus);
                            setIsEditingStatus(true);
                          }}
                        >
                          <Pencil size={14} className="custom-orders-edit-toggle-icon" />
                          <span className="custom-orders-edit-toggle-label-full">Edit order status</span>
                          <span className="custom-orders-edit-toggle-label-mobile">Edit</span>
                        </button>
                      )}
                    </div>
                    <div className="custom-orders-order-status-card-body">
                      {isEditingStatus ? (
                        <div className="custom-orders-order-status-edit-container">
                          <div className="custom-orders-order-status-select-wrapper">
                            <label className="custom-orders-order-status-input-label">Select status to update</label>
                            <select
                              className="custom-orders-order-status-native-select"
                              value={editedStatus}
                              onChange={(e) => setEditedStatus(e.target.value)}
                            >
                              {Object.values(ORDER_STATUS).map(status => (
                                <option key={status} value={status}>
                                  {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="custom-orders-order-status-edit-footer" style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                            <button
                              type="button"
                              className="custom-orders-btn-save"
                              onClick={handleSaveStatus}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              className="custom-orders-btn-cancel"
                              onClick={() => {
                                setIsEditingStatus(false);
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="custom-orders-order-status-info-container">
                          <div className="custom-orders-order-status-content-stack">
                            <div className="custom-orders-order-status-main-display">
                              <span className={`custom-orders-status-pill ${orderDetails.orderStatus?.toLowerCase().replace(' ', '-')}`}>
                                {orderDetails.orderStatus}
                              </span>
                            </div>
                            <div className="custom-orders-order-status-description">
                              <p className="custom-orders-order-status-sub-desc">
                                {getStatusSubtext(orderDetails.orderStatus, orderDetails.packingStatus)}
                              </p>
                            </div>
                          </div>

                          <div className="custom-orders-order-status-interaction">
                            {canEdit && orderDetails.orderStatus === ORDER_STATUS.PENDING && (
                              <button
                                type="button"
                                className="custom-orders-action-stepper-btn confirm"
                                onClick={() => handleUpdateOrderStatus(ORDER_STATUS.CONFIRMED)}
                                disabled={modalLoading}
                              >
                                Confirm Order
                              </button>
                            )}
                            {canEdit && orderDetails.orderStatus === ORDER_STATUS.CONFIRMED && (
                              <button
                                type="button"
                                className="custom-orders-action-stepper-btn primary"
                                onClick={() => handleUpdateOrderStatus(ORDER_STATUS.ON_DELIVERY)}
                                disabled={modalLoading}
                              >
                                Set Out For Delivery
                              </button>
                            )}
                            {canEdit && orderDetails.orderStatus === ORDER_STATUS.ON_DELIVERY && (
                              <button
                                type="button"
                                className="custom-orders-action-stepper-btn success"
                                onClick={() => handleUpdateOrderStatus(ORDER_STATUS.DELIVERED)}
                                disabled={modalLoading}
                              >
                                Set Delivered
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {orderDetails.notes && (
                    <div className="custom-orders-card custom-orders-full-width custom-orders-order-note-card">
                      <h3 className="custom-orders-order-note-title">Order Note</h3>
                      <p className="custom-orders-order-note-text">{orderDetails.notes}</p>
                    </div>
                  )}

                  {orderDetails.cancelRequest?.requestedAt && (
                    <div className="custom-orders-card custom-orders-full-width custom-orders-cancel-request-card">
                      <h3 className="custom-orders-card-title">Cancel Request</h3>
                      <div className="custom-orders-cancel-request-content">
                        {orderDetails.cancelRequest.reason && (
                          <p>Reason: <strong style={{ color: '#dc2626' }}>{orderDetails.cancelRequest.reason}</strong></p>
                        )}
                        <p>Requested at: <strong>{formatDateTime(orderDetails.cancelRequest.requestedAt)}</strong></p>
                        {canEdit && (
                          <div className="custom-orders-cancel-request-actions">
                            <button
                              type="button"
                              className="custom-orders-approve-cancel-btn"
                              onClick={handleApproveCancel}
                              disabled={modalLoading}
                            >
                              Approve & Cancel order
                            </button>
                            <button
                              type="button"
                              className="custom-orders-reject-cancel-btn"
                              onClick={handleRejectCancel}
                              disabled={modalLoading}
                            >
                              Reject this request
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="custom-orders-card custom-orders-half-width">
                    <div className="custom-orders-delivery-schedule-header">
                      <h3 className="custom-orders-card-title">Customer Details</h3>
                      {!isEditingCustomer && canEdit && (
                        <button type="button" className="custom-orders-edit-toggle-btn" onClick={handleStartEditCustomer}>
                          <Pencil size={14} /> Edit
                        </button>
                      )}
                    </div>
                    {isEditingCustomer ? (
                      <div className="custom-orders-delivery-schedule-edit">
                        <div className="custom-orders-delivery-schedule-field">
                          <label className="custom-orders-order-status-input-label">Customer Name</label>
                          <input
                            type="text"
                            value={editCustomerName}
                            onChange={(e) => setEditCustomerName(e.target.value)}
                            className="custom-orders-delivery-date-input"
                          />
                        </div>
                        <div className="custom-orders-delivery-schedule-field">
                          <label className="custom-orders-order-status-input-label">Customer Phone</label>
                          <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: 'white', overflow: 'hidden', width: '100%', boxSizing: 'border-box' }}>
                            <span style={{ padding: '10px 12px', backgroundColor: '#f3f4f6', borderRight: '1px solid #d1d5db', color: '#6b7280', fontWeight: '600', fontSize: '14px', userSelect: 'none', whiteSpace: 'nowrap' }}>+974</span>
                            <input
                              type="text"
                              value={editCustomerPhone}
                              onChange={(e) => {
                                const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 8);
                                setEditCustomerPhone(digitsOnly);
                              }}
                              onBlur={handlePhoneBlur}
                              placeholder="Phone number"
                              maxLength={8}
                              style={{ border: 'none', borderRadius: 0, flex: 1, padding: '10px 12px', margin: 0, outline: 'none', boxShadow: 'none' }}
                            />
                          </div>
                        </div>
                        <div className="custom-orders-delivery-schedule-actions">
                          <button type="button" className="custom-orders-btn-save" onClick={handleSaveCustomer}>
                            Save
                          </button>
                          <button type="button" className="custom-orders-btn-cancel" onClick={() => setIsEditingCustomer(false)}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="custom-orders-row">
                          <span className="custom-orders-label">Name</span>
                          <span className="custom-orders-value">{deliveryAddress.receiversName || '-'}</span>
                        </div>
                        <div className="custom-orders-row">
                          <span className="custom-orders-label">Phone</span>
                          <span className="custom-orders-value">{deliveryAddress.receiversPhoneNumber || '-'}</span>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="custom-orders-card custom-orders-half-width">
                    <div className="custom-orders-delivery-schedule-header">
                      <h3 className="custom-orders-card-title">Customer Address</h3>
                      {!isEditingAddress && canEdit && (
                        <button type="button" className="custom-orders-edit-toggle-btn" onClick={handleStartEditAddress}>
                          <Pencil size={14} /> Edit
                        </button>
                      )}
                    </div>
                    {isEditingAddress ? (
                      <div className="custom-orders-delivery-schedule-edit">
                        <div className="custom-orders-delivery-schedule-field" style={{ marginBottom: '16px' }}>
                          <label className="custom-orders-order-status-input-label">Zone</label>
                          <select
                            value={editAddressZone}
                            onChange={(e) => setEditAddressZone(e.target.value)}
                            className="custom-orders-delivery-slot-select"
                            style={{ maxWidth: '100%' }}
                          >
                            <option value="">-- Select Zone --</option>
                            {ZONE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="custom-orders-delivery-schedule-field" style={{ marginBottom: '16px' }}>
                          <label className="custom-orders-order-status-input-label">Zone Number</label>
                          <input
                            type="text"
                            value={editAddressZoneNumber}
                            onChange={(e) => setEditAddressZoneNumber(e.target.value)}
                            className="custom-orders-delivery-date-input"
                            style={{ maxWidth: '100%' }}
                          />
                        </div>

                        <div className="custom-order-form-row">
                          <div className="custom-orders-delivery-schedule-field">
                            <label className="custom-orders-order-status-input-label">Building Number</label>
                            <input
                              type="text"
                              value={editAddressBuilding}
                              onChange={(e) => setEditAddressBuilding(e.target.value)}
                              className="custom-orders-delivery-date-input"
                            />
                          </div>
                          <div className="custom-orders-delivery-schedule-field">
                            <label className="custom-orders-order-status-input-label">Street</label>
                            <input
                              type="text"
                              value={editAddressStreet}
                              onChange={(e) => setEditAddressStreet(e.target.value)}
                              className="custom-orders-delivery-date-input"
                            />
                          </div>
                        </div>

                        <div className="custom-orders-delivery-schedule-field" style={{ marginBottom: '16px' }}>
                          <label className="custom-orders-order-status-input-label">Full Address</label>
                          <textarea
                            value={editAddressFull}
                            onChange={(e) => setEditAddressFull(e.target.value)}
                            className="custom-orders-delivery-date-input"
                            rows="2"
                            style={{ height: 'auto', resize: 'vertical' }}
                          />
                        </div>

                        <div className="custom-orders-delivery-schedule-field" style={{ marginBottom: '16px' }}>
                          <label className="custom-orders-order-status-input-label">Map Link or Coordinates</label>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <button
                              type="button"
                              className="custom-orders-map-button"
                              onClick={() => {
                                setIsEditPicker(true);
                                setShowMapPicker(true);
                              }}
                              style={{ padding: '0 16px', height: '38px', margin: 0, fontSize: '13px', display: 'flex', alignItems: 'center', backgroundColor: '#10b981', color: 'white', borderColor: '#10b981', flexShrink: 0 }}
                            >
                              Pick Location
                            </button>
                            <input
                              type="text"
                              value={editAddressExactMapLink}
                              onChange={handleEditMapLinkInputChange}
                              className="custom-orders-delivery-date-input"
                              placeholder="Or paste Google Maps link or coordinates here..."
                              style={{ flex: 1, margin: 0, height: '38px' }}
                            />
                          </div>
                          {(() => {
                            const status = getCoordinatesStatus(editAddressExactMapLink, editResolvedCoords);
                            if (!status) return null;
                            if (status.info) {
                              return (
                                <div className="custom-order-coords-below" style={{ marginTop: '8px', padding: '8px 12px', borderRadius: '6px', border: '1px solid #bfdbfe', backgroundColor: '#eff6ff', color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                                  <div style={{ width: '14px', height: '14px', border: '2px solid #3b82f6', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', flexShrink: 0 }}></div>
                                  <span>{status.message}</span>
                                </div>
                              );
                            }
                            if (status.success) {
                              return (
                                <div className="custom-order-coords-below" style={{ marginTop: '8px', padding: '8px 12px', borderRadius: '6px', border: '1px solid #bbf7d0', backgroundColor: '#f0fdf4', color: '#15803d', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                                  <span style={{ color: '#16a34a', fontWeight: 'bold' }}>✓</span>
                                  <span>
                                    {isEditAddressLocationUpdated ? 'Coordinates updated: ' : 'Coordinates: '}
                                    {status.lat}, {status.lng}
                                  </span>
                                </div>
                              );
                            }
                            if (status.warning) {
                              return (
                                <div className="custom-order-coords-below" style={{ marginTop: '8px', padding: '8px 12px', borderRadius: '6px', border: '1px solid #fde68a', backgroundColor: '#fffbeb', color: '#b45309', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                                  <span style={{ color: '#d97706', fontWeight: 'bold' }}>⚠</span>
                                  <span>{status.message}</span>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>

                        <div className="custom-orders-delivery-schedule-actions">
                          <button type="button" className="custom-orders-btn-save" onClick={handleSaveAddress}>
                            Save
                          </button>
                          <button type="button" className="custom-orders-btn-cancel" onClick={() => setIsEditingAddress(false)}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="custom-orders-row">
                          <span className="custom-orders-label">Zone</span>
                          <span className="custom-orders-value">{deliveryAddress.zone || '-'}</span>
                        </div>
                        <div className="custom-orders-row">
                          <span className="custom-orders-label">Building Number</span>
                          <span className="custom-orders-value">{deliveryAddress.buildingNumber || '-'}</span>
                        </div>
                        <div className="custom-orders-row">
                          <span className="custom-orders-label">Zone Number</span>
                          <span className="custom-orders-value">{deliveryAddress.zoneNumber || '-'}</span>
                        </div>
                        <div className="custom-orders-row">
                          <span className="custom-orders-label">Street</span>
                          <span className="custom-orders-value">{deliveryAddress.street || '-'}</span>
                        </div>
                        <div className="custom-orders-row">
                          <span className="custom-orders-label">Full Address</span>
                          <span className="custom-orders-value custom-order-full-address-value">
                            {deliveryAddress.fullAddress || deliveryAddress.address || '-'}
                          </span>
                        </div>
                        {deliveryAddress.exactMapLink && (
                          <>
                            <div className="custom-orders-row">
                              <span className="custom-orders-label">Exact Map Link</span>
                              <span className="custom-orders-value" style={{ wordBreak: 'break-all', whiteSpace: 'normal' }}>
                                <a href={deliveryAddress.exactMapLink} target="_blank" rel="noreferrer" className="custom-orders-order-id-link">
                                  {deliveryAddress.exactMapLink}
                                </a>
                              </span>
                            </div>
                            {deliveryAddress.lat && deliveryAddress.long && (
                              <div className="custom-orders-row">
                                <span className="custom-orders-label">Location</span>
                                <span className="custom-orders-value">
                                  <button type="button" className="custom-orders-map-button" onClick={() => openInMaps(deliveryAddress.lat, deliveryAddress.long)}>
                                    View in Maps
                                  </button>
                                </span>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>

                  <div className="custom-orders-card custom-orders-half-width">
                    <div className="custom-orders-delivery-schedule-header">
                      <h3 className="custom-orders-card-title">Delivery Details</h3>
                      {!isEditingDelivery && canEdit && (
                        <button type="button" className="custom-orders-edit-toggle-btn" onClick={handleStartEditDelivery}>
                          <Pencil size={14} /> Edit
                        </button>
                      )}
                    </div>
                    {isEditingDelivery ? (
                      <div className="custom-orders-delivery-schedule-edit">
                        <div className="custom-orders-delivery-schedule-field">
                          <label className="custom-orders-order-status-input-label">Delivery Date</label>
                          <input
                            type="date"
                            value={editDeliveryDate}
                            onChange={(e) => setEditDeliveryDate(e.target.value)}
                            className="custom-orders-delivery-date-input"
                          />
                        </div>
                        <div className="custom-orders-delivery-schedule-field">
                          <label className="custom-orders-order-status-input-label">Time Slot</label>
                          <select
                            value={editTimeSlot}
                            onChange={(e) => setEditTimeSlot(e.target.value)}
                            className="custom-orders-delivery-slot-select"
                            disabled={loadingFormSlots}
                          >
                            <option value="">{loadingFormSlots ? 'Loading slots...' : '-- Select timeslot --'}</option>
                            {formTimeSlots.map((slot) => (
                              <option key={slot._id || slot.name} value={slot.name}>
                                {slot.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="custom-orders-delivery-schedule-actions">
                          <button type="button" className="custom-orders-btn-save" onClick={handleSaveDelivery}>
                            Save
                          </button>
                          <button type="button" className="custom-orders-btn-cancel" onClick={() => setIsEditingDelivery(false)}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="custom-orders-row">
                          <span className="custom-orders-label">Delivery Date</span>
                          <span className="custom-orders-value">{formatDeliveryDateDisplay(orderDetails.deliveryDate)}</span>
                        </div>
                        <div className="custom-orders-row">
                          <span className="custom-orders-label">Time Slot</span>
                          <span className="custom-orders-value">{orderDetails.timeSlot || '-'}</span>
                        </div>
                      </>
                    )}
                  </div>


                  <div className="custom-orders-details-grid-section">
                    <div className="custom-orders-card">
                      <div className="custom-orders-delivery-schedule-header">
                        <h3 className="custom-orders-card-title">Staff Details</h3>
                      </div>

                      <div className="custom-orders-staff-assignment-group">
                        <div className="custom-orders-staff-assignment-item">
                          <span className="custom-orders-label">Packing Staff</span>
                          {orderDetails.packingStaff?.name ? (
                            <div className="custom-orders-staff-assigned">
                              <div className="custom-orders-staff-info">
                                <span className="custom-orders-value">{orderDetails.packingStaff.name}</span>
                                <span className="custom-orders-staff-phone">{orderDetails.packingStaff.phoneNumber}</span>
                              </div>
                              {canEdit && orderDetails.orderStatus !== 'delivered' && orderDetails.orderStatus !== 'cancelled' && (
                                <button
                                  type="button"
                                  className="custom-orders-staff-replace-btn"
                                  onClick={() => openStaffSelect('packing')}
                                >
                                  Replace
                                </button>
                              )}
                            </div>
                          ) : canEdit && (orderDetails.orderStatus === 'pending' || orderDetails.orderStatus === 'confirmed' || orderDetails.orderStatus === 'processing') ? (
                            <button
                              type="button"
                              className="custom-orders-staff-assign-btn"
                              onClick={() => openStaffSelect('packing')}
                            >
                              Assign Packing Staff
                            </button>
                          ) : (
                            <span className="custom-orders-value">-</span>
                          )}
                        </div>

                        <div className="custom-orders-staff-assignment-item">
                          <span className="custom-orders-label">Delivery Staff</span>
                          {orderDetails.deliveryBoyDetails?.name ? (
                            <div className="custom-orders-staff-assigned">
                              <div className="custom-orders-staff-info">
                                <span className="custom-orders-value">{orderDetails.deliveryBoyDetails.name}</span>
                                <span className="custom-orders-staff-phone">{orderDetails.deliveryBoyDetails.phoneNumber}</span>
                              </div>
                              {canEdit && orderDetails.orderStatus !== 'delivered'
                                && orderDetails.orderStatus !== 'cancelled'
                                && !orderDetails.dispatchDetails && (
                                <button
                                  type="button"
                                  className="custom-orders-staff-replace-btn"
                                  onClick={() => openStaffSelect('delivery')}
                                >
                                  Replace
                                </button>
                              )}
                              {canEdit && orderDetails.dispatchDetails
                                && orderDetails.orderStatus !== 'delivered'
                                && orderDetails.orderStatus !== 'cancelled'
                                && onEditInDispatch && (
                                <button
                                  type="button"
                                  className="custom-orders-staff-dispatch-btn"
                                  onClick={() => onEditInDispatch(orderDetails.dispatchDetails)}
                                  disabled={dispatchLoading}
                                >
                                  {dispatchLoading ? 'Loading...' : 'Edit in Dispatch'}
                                </button>
                              )}
                            </div>
                          ) : canEdit && (orderDetails.orderStatus === 'pending' || orderDetails.orderStatus === 'confirmed' || orderDetails.orderStatus === 'processing') ? (
                            <button
                              type="button"
                              className="custom-orders-staff-assign-btn"
                              onClick={() => openStaffSelect('delivery')}
                            >
                              Assign Delivery Staff
                            </button>
                          ) : (
                            <span className="custom-orders-value">-</span>
                          )}
                        </div>
                      </div>

                      {orderDetails.orderStatus !== 'cancelled' && (
                        <button
                          type="button"
                          className="custom-orders-staff-share-btn"
                          onClick={openStaffShareModal}
                        >
                          Share via WhatsApp
                        </button>
                      )}
                    </div>

                    {timeline.length > 0 && (
                      <div className="custom-orders-card">
                        <h3 className="custom-orders-card-title">Order Timeline</h3>
                        <div className="custom-orders-timeline">
                          {timeline.map((item, index) => (
                            <div key={`${item.label}-${index}`} className="custom-orders-timeline-item">
                              <div className="custom-orders-timeline-marker">
                                <div className="custom-orders-timeline-dot"></div>
                                {index !== timeline.length - 1 && (
                                  <div className="custom-orders-timeline-line"></div>
                                )}
                              </div>
                              <div className="custom-orders-timeline-content">
                                <div className="custom-orders-timeline-label">{item.label}</div>
                                <div className="custom-orders-timeline-time">{item.time}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="custom-orders-card custom-orders-half-width custom-orders-bill-details-card">
                    <div className="custom-orders-delivery-schedule-header">
                      <h3 className="custom-orders-card-title">Bill Summary</h3>
                      {!isEditingBill && canEdit && (
                        <button type="button" className="custom-orders-edit-toggle-btn" onClick={handleStartEditBill}>
                          <Pencil size={14} /> Edit
                        </button>
                      )}
                    </div>

                    {isEditingBill ? (
                      <div className="custom-orders-delivery-schedule-edit">
                        <div className="custom-orders-delivery-schedule-field" style={{ marginBottom: '16px' }}>
                          <label className="custom-orders-order-status-input-label">Total Price (QR)</label>
                          <input
                            type="number"
                            value={editTotalPrice}
                            onChange={(e) => setEditTotalPrice(e.target.value)}
                            className="custom-orders-delivery-date-input"
                            step="0.01"
                            min="0"
                          />
                        </div>

                        <div className="custom-orders-delivery-schedule-actions">
                          <button type="button" className="custom-orders-btn-save" onClick={handleSaveBill}>
                            Save
                          </button>
                          <button type="button" className="custom-orders-btn-cancel" onClick={() => setIsEditingBill(false)}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="custom-orders-bill-row">
                          <span className="custom-orders-bill-label">Item Total</span>
                          <span className="custom-orders-bill-value">{Number(orderDetails.totalPrice || 0).toFixed(2)} QR</span>
                        </div>

                        <div className="custom-orders-bill-row">
                          <span className="custom-orders-bill-label">Delivery Charge</span>
                          <span className="custom-orders-bill-value">0.00 QR</span>
                        </div>

                        <div className="custom-orders-bill-row custom-orders-bill-total">
                          <span className="custom-orders-bill-label">All Total</span>
                          <span className="custom-orders-bill-value">{Number(orderDetails.totalPrice || 0).toFixed(2)} QR</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {showStaffSelectModal && (
        <div className="staff-selection-overlay" onClick={closeStaffSelectModal}>
          <div className="staff-selection-modal" onClick={(e) => e.stopPropagation()}>
            <div className="staff-selection-header">
              <h3>
                {staffModalMode === 'share'
                  ? 'Share via WhatsApp'
                  : `Select ${staffAssignmentType === 'packing' ? 'Packing' : 'Delivery'} Staff`}
              </h3>
              <button type="button" className="staff-selection-close" onClick={closeStaffSelectModal}>×</button>
            </div>
            <div className="staff-selection-body">
              {getStaffModalDrivers().length === 0 && staffModalMode !== 'share' ? (
                <p className="staff-selection-empty">No active staff found.</p>
              ) : (
                <div className="staff-selection-grid">
                  {getStaffModalDrivers().map(staff => {
                    const staffId = staff.id;
                    const isSelected = staffModalMode !== 'share' && (
                      staffAssignmentType === 'packing'
                        ? orderDetails.packingStaff?.id
                        : orderDetails.deliveryBoyDetails?.id
                    ) === staffId;
                    return (
                      <div
                        key={staffId}
                        className={`staff-selection-item ${isSelected ? 'active' : ''} ${staffActionLoading ? 'disabled' : ''}`}
                        onClick={() => {
                          if (staffActionLoading) return;
                          if (staffModalMode === 'share') {
                            handleShareToStaff(staff);
                          } else {
                            handleAssignStaff(staff);
                          }
                        }}
                        style={{
                          pointerEvents: staffActionLoading ? 'none' : 'auto',
                          opacity: staffActionLoading ? 0.7 : 1,
                          cursor: staffActionLoading ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <span className="staff-selection-name">{staff.name}</span>
                      </div>
                    );
                  })}
                  {staffModalMode === 'share' && (
                    <div
                      className="staff-selection-item"
                      onClick={handleShareToWhatsAppOther}
                    >
                      <span className="staff-selection-name">Other</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <MapPickerModal
        isOpen={showMapPicker}
        onClose={() => {
          setShowMapPicker(false);
          setIsEditPicker(false);
        }}
        initialCoords={isEditPicker ? (extractCoordinatesFromUrl(editAddressExactMapLink) ? [extractCoordinatesFromUrl(editAddressExactMapLink).lng, extractCoordinatesFromUrl(editAddressExactMapLink).lat] : null) : (extractCoordinatesFromUrl(formData.mapLink) ? [extractCoordinatesFromUrl(formData.mapLink).lng, extractCoordinatesFromUrl(formData.mapLink).lat] : null)}
        onConfirm={handleMapConfirm}
      />



    </>
  );
};

export default CustomOrderModals;
